import React, { FormEvent } from 'react';
import { Head, Link, useForm } from '@inertiajs/react';
import DashboardLayout from '../../../components/DashboardLayout';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card, CardContent } from '../../../components/ui/card';

interface Document {
  id: number;
  title: string;
  content: string;
  policy_type: string;
  updated_at: string;
}

interface EditProps {
  document: Document;
  errors?: string[];
}

const Edit: React.FC<EditProps> = ({ document, errors = [] }) => {
  const { data, setData, put, processing, errors: formErrors } = useForm({
    title: document.title || '',
    content: document.content || '',
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    put(`/admin/legal_documents/${document.id}`);
  }

  return (
    <DashboardLayout>
      <Head title={`Edit ${document.title} - Admin`} />

      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Edit {document.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Update the {document.policy_type === 'privacy' ? 'Privacy Policy' : 'Terms of Service'} content.
            </p>
          </div>
          <Link href="/admin/legal_documents">
            <Button variant="outline">Back to Legal Documents</Button>
          </Link>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            <ul className="list-disc list-inside">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Form */}
        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Document Title</Label>
                <Input
                  id="title"
                  name="legal_document[title]"
                  type="text"
                  value={data.title}
                  onChange={(e) => setData('title', e.target.value)}
                  required
                />
                {formErrors['title'] && (
                  <p className="text-sm text-red-600">{formErrors['title']}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">
                  Content (Markdown supported)
                </Label>
                <Textarea
                  id="content"
                  name="legal_document[content]"
                  value={data.content}
                  onChange={(e) => setData('content', e.target.value)}
                  rows={20}
                  className="font-mono text-sm"
                  placeholder="Enter markdown content here..."
                  required
                />
                {formErrors['content'] && (
                  <p className="text-sm text-red-600">{formErrors['content']}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Supports Markdown formatting: **bold**, *italic*, # headings, - lists, [links](url), etc.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <Link href="/admin/legal_documents">
                  <Button type="button" variant="outline">Cancel</Button>
                </Link>
                <Button type="submit" disabled={processing}>
                  {processing ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Edit;
